import { haptic, useTheme } from '@kilat/ui';

import { useI18n } from '../lib/use-i18n.js';

/**
 * Round glass theme toggle for the header. Sun in dark mode (→ switch to
 * light), moon in light mode (→ switch to dark). The action, not the current
 * state, is announced to assistive tech. A light haptic tap fires on press
 * (no-op on desktop / reduced-motion).
 */
export function ThemeToggle() {
  const { resolved, setChoice } = useTheme();
  const { t } = useI18n();
  // `resolved` rather than the raw choice: the icon must show what is on
  // screen right now, which for a viewer on 'system' is whatever their OS says.
  const isDark = resolved === 'dark';
  const label = isDark ? t('marketing.theme.toLight') : t('marketing.theme.toDark');

  return (
    <button
      type="button"
      className="lift"
      onClick={() => {
        haptic.tap();
        // Explicit both ways. Flipping to the opposite of what is CURRENTLY
        // shown is what the icon promises, and it moves a 'system' viewer to a
        // real choice rather than to the other end of a cycle they never saw.
        setChoice(isDark ? 'light' : 'dark');
      }}
      aria-label={label}
      title={label}
      style={{
        minWidth: '2.75rem',
        minHeight: '2.75rem',
        width: '2.75rem',
        height: '2.75rem',
        borderRadius: '999px',
        border: '1px solid var(--glass-border)',
        background: 'var(--glass-bg)',
        color: 'var(--color-on-surface)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        padding: 0,
        flexShrink: 0,
      }}
    >
      {isDark ? <SunIcon /> : <MoonIcon />}
    </button>
  );
}

function SunIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden focusable="false">
      <circle cx="12" cy="12" r="4.2" fill="currentColor" />
      <g stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <path d="M12 2.5v2.6M12 18.9v2.6M4.4 4.4l1.8 1.8M17.8 17.8l1.8 1.8M2.5 12h2.6M18.9 12h2.6M4.4 19.6l1.8-1.8M17.8 6.2l1.8-1.8" />
      </g>
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden focusable="false">
      <path d="M20 14.4A8 8 0 1 1 9.6 4a6.4 6.4 0 0 0 10.4 10.4Z" fill="currentColor" />
    </svg>
  );
}
